import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Button, buttonClass } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useI18n } from '@/hooks/useI18n';
import { useSesion } from './identidad/useSesion';
import { useMensajeDeAcceso } from './identidad/useMensajeDeAcceso';
import { RUTA_PANEL, RUTA_RECUPERAR } from './rutas';
import styles from './area.module.css';

/**
 * La puerta del área de inversionistas.
 *
 * **Reescrita, no recuperada.** La versión del historial (`073f62f^`, 356
 * líneas) era un alta de autoservicio: un formulario de seis campos —fondo,
 * cargo, sitio web, ticket típico, interés— que comprobaba una lista de
 * permitidos y dejaba **en cola** a quien no estuviera, con siete estados
 * distintos para contarlo. Cinco de esos siete describían esa cola.
 *
 * Nada de eso existe ya, y el motivo no es de producto: **SES está en sandbox**
 * en las cuentas donde vive el backend, y en sandbox un correo a un
 * destinatario cualquiera se acepta y **nunca llega**. Prometer «te avisamos por
 * correo» habría sido prometer algo que el sistema no puede cumplir, que es peor
 * que no ofrecerlo. Así que el acceso es por invitación, las cuentas se cargan a
 * mano, y aquí quedan dos campos. Decidido por Alan el 2026-09-20.
 *
 * Lo que sí se conserva del original: que la pantalla **diga lo que se va a ver
 * antes de pedir nada**. Quien evalúa invertir merece saber si vale la pena
 * escribir un correo.
 */
export function AccesoInversionistaPage() {
  const { t } = useI18n();
  const a = t.area.acceso;
  const mensajeDeError = useMensajeDeAcceso();
  const ubicacion = useLocation();
  const { sesion, rehidratando, esDeMentira, iniciarSesion } = useSesion();

  useDocumentMeta(t.meta.accesoInversionista);

  const [correo, setCorreo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [errores, setErrores] = useState<{ correo?: string; contrasena?: string }>({});
  const [falloGeneral, setFalloGeneral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  /*
    Ver la contraseña mientras se escribe.

    Arranca oculta **siempre**, y no se recuerda entre visitas: el estado vive
    en el componente y se va al navegar. Una preferencia guardada aquí
    significaría enseñar la contraseña por defecto en un portátil que alguien
    abrió en una cafetería, que es el escenario exacto contra el que el campo
    está oculto de origen.
  */
  const [verContrasena, setVerContrasena] = useState(false);

  // Con sesión, esta pantalla no tiene nada que hacer: manda al panel. El
  // `state.from` lo pone el guardia cuando rebota a alguien, para devolverlo a
  // donde iba y no al índice del área.
  if (!rehidratando && sesion) {
    const destino = (ubicacion.state as { from?: string } | null)?.from ?? RUTA_PANEL;
    return <Navigate to={destino} replace />;
  }

  async function alEnviar(evento: FormEvent) {
    evento.preventDefault();

    // Validación a mano y por campo, que es como se hace en este repo: no hay
    // react-hook-form ni zod en el `package.json`, y no se añaden por dos
    // campos.
    const nuevos: { correo?: string; contrasena?: string } = {};
    if (!correo.trim()) nuevos.correo = a.correoRequerido;
    if (!contrasena) nuevos.contrasena = a.contrasenaRequerida;
    setErrores(nuevos);
    setFalloGeneral(null);
    if (Object.keys(nuevos).length > 0) return;

    setEnviando(true);
    try {
      await iniciarSesion(correo, contrasena);
      // No se navega desde aquí: al haber sesión, el `Navigate` de arriba se
      // encarga en el siguiente render. Un `navigate()` además de eso sería
      // dos caminos para lo mismo.
    } catch (error) {
      setFalloGeneral(mensajeDeError(error));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className="ilu-container">
        <header className={styles.head}>
          <h1 className={styles.title}>{a.titulo}</h1>
          <p className={styles.subtitle}>{a.subtitulo}</p>
        </header>

        <div className={[styles.card, styles.puerta].join(' ')}>
          {/*
            Que el login es de mentira se dice en pantalla mientras lo sea, y lo
            decide el puerto —`esDeMentira`—, no esta pantalla: así el aviso se
            apaga solo el día que se monte el adaptador de verdad, sin tocar
            aquí. Un login falso que no se anuncia tiene la forma exacta de una
            pantalla de phishing, y alguien acabaría escribiendo su contraseña
            real: por eso el texto lo dice con todas las letras.

            Con copy propio y no el de `/demo`: aquél habla de trayectorias y
            cifras inventadas y cierra con «no corresponden a un evento real»,
            que de un formulario de acceso es sencillamente falso.
          */}
          {esDeMentira && (
            <p className={[styles.aviso, styles.avisoError].join(' ')} role="status">
              <AlertTriangle size={16} aria-hidden="true" style={{ verticalAlign: '-3px' }} />{' '}
              {a.deMentiraAntes}
              <strong>{a.deMentiraFuerte}</strong>
              {a.deMentiraDespues}
            </p>
          )}

          <p className={[styles.aviso, styles.avisoNeutro].join(' ')}>
            <ShieldCheck size={16} aria-hidden="true" style={{ verticalAlign: '-3px' }} />{' '}
            {a.avisoAntes}
            <strong>{a.avisoFuerte}</strong>
            {a.avisoDespues}
          </p>

          <p className={styles.metricHint} style={{ marginBottom: 20 }}>
            {a.porInvitacion}
          </p>

          <form onSubmit={(evento) => void alEnviar(evento)} noValidate>
            {/*
              El fallo del intento va antes de los campos y con `role="alert"`:
              interrumpe —alguien acaba de pulsar y quiere saber qué pasó— a
              diferencia de los avisos de arriba, que son informativos.
            */}
            {falloGeneral && (
              <p className={[styles.aviso, styles.avisoError].join(' ')} role="alert">
                {falloGeneral}
              </p>
            )}

            <div className={styles.field}>
              <label htmlFor="correo">{a.correo}</label>
              <input
                id="correo"
                name="correo"
                type="email"
                autoComplete="username"
                value={correo}
                onChange={(evento) => setCorreo(evento.target.value)}
                aria-invalid={Boolean(errores.correo)}
                aria-describedby={errores.correo ? 'correo-error' : undefined}
              />
              {errores.correo && (
                <p className={styles.error} id="correo-error">
                  {errores.correo}
                </p>
              )}
            </div>

            <div className={[styles.field, styles.campoConOjo].join(' ')}>
              <label htmlFor="contrasena">{a.contrasena}</label>
              <input
                id="contrasena"
                name="contrasena"
                type={verContrasena ? 'text' : 'password'}
                autoComplete="current-password"
                /*
                  Con `type="text"` el campo deja de ser un campo de contraseña
                  para el navegador, y eso tiene consecuencias que no se ven:
                  el corrector ortográfico **mejorado** de Chrome y Edge manda
                  el contenido de los campos de texto a un servidor para
                  revisarlo. Una contraseña visible no debe viajar a ningún
                  lado por haber pulsado un ojo.

                  `autoCapitalize` y `autoCorrect` van por lo mismo en móvil:
                  un teclado que "arregla" una contraseña la rompe en silencio
                  y el error que sale después es «credenciales».
                */
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
                aria-invalid={Boolean(errores.contrasena)}
                aria-describedby={errores.contrasena ? 'contrasena-error' : undefined}
                value={contrasena}
                onChange={(evento) => setContrasena(evento.target.value)}
              />
              {/*
                `type="button"` y no un `<button>` a secas: dentro de un
                `<form>`, un botón sin tipo es `submit` por omisión, así que el
                ojo intentaría entrar con la contraseña a medio escribir.

                La etiqueta **cambia** con el estado en vez de quedarse fija con
                un `aria-pressed`. Las dos formas son válidas; ésta se oye mejor
                en un lector —«Mostrar contraseña, botón» y luego «Ocultar
                contraseña, botón»— y no depende de que la ayuda técnica
                anuncie bien el estado de presionado.

                No lleva `aria-hidden` en el icono por descuido: lo lleva
                porque el nombre accesible ya lo pone `aria-label`, y sin eso el
                lector diría el nombre del `svg` además.
              */}
              <button
                type="button"
                className={styles.ojo}
                onClick={() => setVerContrasena((visible) => !visible)}
                aria-label={verContrasena ? a.ocultarContrasena : a.mostrarContrasena}
                aria-controls="contrasena"
              >
                {verContrasena ? (
                  <EyeOff size={18} aria-hidden="true" />
                ) : (
                  <Eye size={18} aria-hidden="true" />
                )}
              </button>
              {errores.contrasena && (
                <p className={styles.error} id="contrasena-error">
                  {errores.contrasena}
                </p>
              )}
            </div>

            <div className={styles.acciones}>
              <Button type="submit" disabled={enviando}>
                {enviando ? a.entrando : a.entrar}
              </Button>
              <Link to="/contacto" className={buttonClass({ variant: 'ghost' })}>
                {a.escribirnos}
              </Link>
            </div>

            {/*
              Debajo de las acciones y no al lado del campo: el camino principal
              es entrar, y un enlace de «olvidé mi contraseña» junto al campo
              compite con el botón justo cuando alguien SÍ se acuerda de ella.
            */}
            <div style={{ marginTop: 16 }}>
              <Link to={RUTA_RECUPERAR}>{a.olvidaste}</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
